import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';

/**
 * Landing page that the confirm-your-new-email link points to. Reads the
 * `token` query param, POSTs it to /api/auth/email-change/confirm, and
 * shows success / error. User then logs in again with the new email.
 */
const EmailChangeConfirmPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Confirming your email change…');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('No confirmation token in the link.');
      return;
    }
    api
      .post('/auth/email-change/confirm', { token })
      .then(() => {
        setState('ok');
        setMessage('Email updated. Please log in with your new address.');
      })
      .catch(err => {
        setState('error');
        setMessage(
          err?.response?.data?.message ||
            'Could not confirm the email change. The link may have expired.',
        );
      });
  }, [token]);

  return (
    <main className="bg-cream min-h-[60vh]">
      <Helmet>
        <title>Confirm email change — EJL Education</title>
      </Helmet>
      <section className="section">
        <div className="container-content max-w-[56ch]">
          <h1 className="mb-4">
            {state === 'ok' ? 'Email updated' : state === 'error' ? 'Confirmation failed' : 'Confirming…'}
          </h1>
          <p className="text-body text-ink/90 mb-6">{message}</p>
          {state === 'ok' && (
            <Link to="/login" className="btn btn-primary">
              Log in with your new email
            </Link>
          )}
          {state === 'error' && (
            <Link to="/dashboard" className="btn btn-secondary">
              Back to dashboard
            </Link>
          )}
        </div>
      </section>
    </main>
  );
};

export default EmailChangeConfirmPage;
